<template>
  <div class="space-y-2">
    <div class="flex flex-wrap items-end gap-4">
      <!-- Field of the surrounding transfer form; saved with the other transfer fields. -->
      <InputField
        class="min-w-64 flex-1"
        name="externalDriverEmail"
        type="email"
        :label="t('externalDriverEmail')"
        :placeholder="t('noExternalDriver')"
        empty-value-to="null"
      />
      <Select
        v-model="language"
        class="w-40"
        :label="t('mailLanguage')"
        :options="languageOptions"
      />
      <CVButton :is-loading="isPending" :disabled="!!blockedReason" @click.prevent="handleSend">
        <SendIcon class="size-4" />
        {{ t('sendLink') }}
      </CVButton>
    </div>
    <p v-if="blockedReason" class="text-sm text-gray-600">{{ t(blockedReason) }}</p>
    <p v-else class="text-sm text-gray-600">{{ t('replacesEarlierLinks') }}</p>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { SendIcon } from 'lucide-vue-next';
import { LanguageCode } from '@carvia/ros-client-types';
import CVButton from '@/components/buttons/CVButton.vue';
import Select from '@/components/Select.vue';
import InputField from '@/components/InputField.vue';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import { useSendExternalHandoverLink } from '@/queries/use-external-handover';

const props = defineProps<{
  transfer: Allocation;
  hasUnsavedChanges: boolean;
}>();

const { t } = useI18n();

const language = ref<LanguageCode>(LanguageCode.DE);
const languageOptions = [
  { label: 'Deutsch', value: LanguageCode.DE },
  { label: 'English', value: LanguageCode.EN },
];

const blockedReason = computed(() => {
  if (props.hasUnsavedChanges) return 'saveFirst';
  if (!props.transfer.externalDriverEmail) return 'noEmail';
  return null;
});

const { mutateAsync: sendLink, isPending } = useSendExternalHandoverLink();

const handleSend = async () => {
  await sendLink({ deliveryTransferId: props.transfer.id, language: language.value });
};
</script>

<i18n lang="json">
{
  "en": {
    "externalDriverEmail": "External driver email",
    "noExternalDriver": "No external driver",
    "mailLanguage": "Mail language",
    "sendLink": "Send handover link",
    "replacesEarlierLinks": "Sending a new link makes earlier links stop working.",
    "saveFirst": "Save the email address first.",
    "noEmail": "Enter the external driver's email to send a handover link."
  },
  "de": {
    "externalDriverEmail": "E-Mail externer Fahrer",
    "noExternalDriver": "Kein externer Fahrer",
    "mailLanguage": "Sprache der Mail",
    "sendLink": "Übergabelink senden",
    "replacesEarlierLinks": "Ein neuer Link ersetzt alle zuvor gesendeten Links.",
    "saveFirst": "Bitte speichere zuerst die E-Mail-Adresse.",
    "noEmail": "Trage die E-Mail des externen Fahrers ein, um einen Übergabelink zu senden."
  },
  "es": {
    "externalDriverEmail": "Correo del conductor externo",
    "noExternalDriver": "Sin conductor externo",
    "mailLanguage": "Idioma del correo",
    "sendLink": "Enviar enlace de entrega",
    "replacesEarlierLinks": "Un enlace nuevo deja sin efecto los enviados antes.",
    "saveFirst": "Guarda primero la dirección de correo.",
    "noEmail": "Introduce el correo del conductor externo para enviar un enlace de entrega."
  }
}
</i18n>
